import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import TextField from "@/common/components/TextField";
import { zodResolver } from "@hookform/resolvers/zod";
import { BriefcaseBusinessIcon, ChevronsRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { ShowHeader } from "@/common/components/ShowHeader";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import { useCompany } from "@/features/privateCompanies/hooks/useCompany";
import AccordionScrapRulesOverview from "../../../components/maintenanceScrapRules/AccordionOverview";
import AccordionScrapRulesEquipments from "../../../components/maintenanceScrapRules/AccordionEquipments";
import { CreateScrapRuleSchema, createScrapRuleSchema } from "../../../schemas/maintenanceScrapRules/createScrapRule.schema";
import AccordionScrapRulesExecutionRules from "../../../components/maintenanceScrapRules/AccordionExecutionRules";
import useScrapRule from "../../../hooks/maintenanceScrapRules/useScrapRule";
import { ModalDeleteScrapRule } from "../../../components/maintenanceScrapRules/ModalDelete";
import { ErrorResponse } from "@/common/types/errorResponse";
import { ModalErrorMessage } from "@/common/components/ModalErrorMessage";
import { ScrapRulesTransformer } from "@/features/privateCompanies/services/maintenanceScrapRules/scrapRulesTransformer";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import useUserPermissions from "@/common/hooks/useUserPermissions";

const Show = () => {
  const { companyId, id } = useParams();

  const navigate = useNavigate();

  const { data: companyData } = useCompany(companyId || '');

  const [messages, setMessages] = useState<string[] | boolean>(false)
  const [errorData, setErrorData] = useState<ErrorResponse | boolean>(false)

  const {
    data: scrapRule,
    isLoading: scrapRuleIsLoading,
    error: scrapRuleError = {} as any,
    refetch: refetchScrapRule
   } = useScrapRule(String(id))

  const {
    register,
    control,
    reset,
    formState: { errors }
  } = useForm<CreateScrapRuleSchema>({
    resolver: zodResolver(createScrapRuleSchema)
  });

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const userHasPermissionToSeeMaintenanceScrapRules =
    isUserPermissionsPending || hasUserPermission('Admin::Maintenance::ScrapRules::Show')


  useEffect(() => {
    if (!scrapRuleIsLoading && scrapRule) {
      const formData = ScrapRulesTransformer.formDataTransformer(scrapRule)
      reset(formData)
    }
  }, [scrapRuleIsLoading])

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      <ShowHeader
        goBack
        goBackProps={{
          fallback: `/companies/${companyId}/maintenance/scrap-rules`,
          searchLastRouteFromFallback: true
        }}
        title={
          <div className="flex gap-2 items-center justify-start">
            <div className="flex gap-2 items-center">
              <ImageWithPlaceholder
                url={companyData?.general_information?.company_logo?.url}
                alt={companyData?.general_information?.fantasy_name}
                className="size-8"
                iconClassName="size-6"
                placeholderIcon={BriefcaseBusinessIcon}
              />
              <p className="text-white-400 font-normal text-base line-clamp-1">{companyData?.general_information?.fantasy_name}</p>
            </div>
            <ChevronsRight color='#ffffffb3' />
            <h2 className="text-xl font-bold text-ellipsis overflow-hidden whitespace-nowrap max-w-full line-clamp-1">Regras para desfazimento</h2>
          </div>
        }
        subtitle={scrapRule?.display_name}
        buttons={
          userHasPermissionToSeeMaintenanceScrapRules ? (
            <div className="flex items-center gap-4">
              <ModalDeleteScrapRule
                scrapRuleId={id}
                companyId={companyId}
              />
              <Button
                operationKlassToCheckPermission="Admin::Maintenance::ScrapRules::Update"
                onClick={() => navigate('edit')}
              >
                Editar
              </Button>
            </div>
          ) : undefined
        }
      />

      <ErrorMessageWrapper
        statusCode={scrapRuleError?.status}
        refetchFunction={refetchScrapRule}
      >
        <Box className="flex flex-col flex-1 overflow-auto gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          <AccordionScrapRulesOverview />

          <div className="flex gap-2">
            <TextField
              label="Nome da regra de desfazimento"
              markAsRequired
              errorMessage={errors?.display_name?.message}
              {...register('display_name')}
              className="w-full"
              disabled
            />
          </div>

          <AccordionScrapRulesEquipments
            control={control}
            register={register}
            errors={errors}
            disabled
          />

          <AccordionScrapRulesExecutionRules
            control={control}
            register={register}
            errors={errors}
            disabled
          />
        </Box>

        {messages && (
          <ModalMessageSimple
            messages={messages}
            setMessages={setMessages}
            isError
          />
        )}

        <ModalErrorMessage
          data={errorData as ErrorResponse}
          setData={setErrorData as React.Dispatch<React.SetStateAction<boolean>>}
          title="Deleção não concluída"
          message="Algo deu errado e a deleção não foi concluída."
        />
      </ErrorMessageWrapper>
    </div>
  );
};

export default Show;
